import { formConvertErrorMessage } from "helpers/form_helper";
import { errorMessage } from "helpers/utils";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "react-query";
import { useToasts } from "react-toast-notifications";
import shippingUnitService from "services/shippingUnitService";

// List Shipping Unit
export const useListShippingUnit = (payload, onSuccess, onError) => {
  const { t } = useTranslation(["shippingUnit"]);
  const { addToast } = useToasts();

  const { params } = payload;

  return useQuery(
    ["shipping-units", params?.page],
    () => shippingUnitService.listShippingUnit(payload),
    {
      enabled: false,
      retry: 1,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.list", {
                target: t("shipping_unit"),
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        const { data } = response;
        return {
          items: data.items || [],
          params: params,
          totalCount: data._meta ? data._meta.totalCount : data.items.length,
        };
      },
    }
  );
};

// Create Shipping Unit
export const useCreateShippingUnit = (onSuccess, onError) => {
  const { t, i18n } = useTranslation(["shippingUnit"]);
  const { addToast } = useToasts();

  return useMutation(shippingUnitService.createShippingUnit, {
    retry: 0,
    onSuccess: data => {
      addToast(
        t("message.create", {
          target: t("shipping_unit"),
          status: t("message.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.create", {
              target: t("shipping_unit"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

// Detail Shipping Unit
export const useDetailShippingUnit = (payload, onSuccess, onError) => {
  const { t, i18n } = useTranslation(["shippingUnit"]);
  const { addToast } = useToasts();
  return useQuery(
    ["shipping-unit", "detail", payload],
    () => shippingUnitService.detailShippingUnit(payload),
    {
      enabled: true,
      retry: 1,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.detail", {
                target: t("shipping_unit"),
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        const { data } = response;
        return data;
      },
    }
  );
};

// Update Shipping Unit
export const useUpdateShippingUnit = (onSuccess, onError) => {
  const { t, i18n } = useTranslation(["shippingUnit"]);
  const { addToast } = useToasts();

  return useMutation(shippingUnitService.updateShippingUnit, {
    retry: 0,
    onSuccess: data => {
      addToast(
        t("message.update", {
          target: t("shipping_unit"),
          status: t("message.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.update", {
              target: t("shipping_unit"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

// Active Shipping Unit
export const useActiveShippingUnit = (onSuccess, onError) => {
  const { t } = useTranslation(["shippingUnit"]);
  const { addToast } = useToasts();

  return useMutation(shippingUnitService.activeShippingUnit, {
    retry: 0,
    onSuccess: response => {
      const { data } = response;

      addToast(
        t("message.active", {
          target: t("shipping_unit"),
          status: t("message.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.active", {
              target: t("shipping_unit"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

// Delete Shipping Unit
export const useDeleteShippingUnit = (onSuccess, onError) => {
  const { t } = useTranslation(["shippingUnit"]);
  const { addToast } = useToasts();

  return useMutation(shippingUnitService.deleteShippingUnit, {
    retry: 0,
    onSuccess: response => {
      const { data } = response;

      addToast(
        t("message.delete", {
          target: t("shipping_unit"),
          status: t("message.success"),
        }),
        {
          appearance: "success",
          autoDismiss: true,
        }
      );
      if (typeof onSuccess === "function") {
        onSuccess(data);
      }
    },
    onError: error => {
      const errorResponse = errorMessage(error);
      addToast(
        formConvertErrorMessage(
          errorResponse.message ||
            t("message.delete", {
              target: t("shipping_unit"),
              status: t("message.error"),
            }),
          t
        ),
        {
          appearance: "error",
          autoDismiss: true,
        }
      );
      if (typeof onError === "function") {
        onError(errorResponse);
      }
    },
  });
};

// List Shipping Unit All
export const useListShippingUnitAll = (onSuccess, onError) => {
  const { t } = useTranslation(["shippingUnit"]);
  const { addToast } = useToasts();

  return useQuery(
    ["shipping-units-all"],
    () => shippingUnitService.listShippingUnitAll(),
    {
      enabled: false,
      retry: 1,
      onSuccess,
      onError: error => {
        const errorResponse = errorMessage(error);
        addToast(
          formConvertErrorMessage(
            errorResponse.message ||
              t("message.list", {
                target: t("shipping_unit"),
                status: t("message.error"),
              }),
            t
          ),
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
        if (typeof onError === "function") {
          onError(errorResponse);
        }
      },
      select: response => {
        const { data } = response;
        return data || [];
      },
    }
  );
};
